import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { metricsService } from '@services/api/metricsService';
import { useAuthStore } from '@stores/authStore';
import { getCasePriorityLabel, getCasePriorityColor, calculateSLAStatus } from '@/types/case';
import './PriorityQueue.css';

export const PriorityQueue = () => {
  const { user } = useAuthStore();
  const [cases, setCases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadPriorityQueue();
    }
  }, [user]);

  const loadPriorityQueue = async () => {
    if (!user) return;
    
    setLoading(true);
    const result = await metricsService.getMyPriorityQueue(user.id, 5);
    
    if (result.success && result.data) {
      setCases(result.data);
    }
    
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="priority-queue-card">
        <h3 className="priority-queue-title">📌 My Priority Queue</h3>
        <p className="text-secondary">Loading...</p>
      </div>
    );
  }

  return (
    <div className="priority-queue-card">
      <h3 className="priority-queue-title">📌 My Priority Queue</h3>
      
      {cases.length === 0 ? (
        <p className="text-secondary">No cases assigned to you</p>
      ) : (
        <div className="priority-queue-list">
          {cases.map((c) => {
            const slaStatus = calculateSLAStatus(
              c.created_at,
              c.sla_due_at,
              c.status
            );

            return (
              <Link
                key={c.id}
                to={`/cases/${c.id}`}
                className="priority-queue-item"
              >
                <div className="queue-item-header">
                  <span className={`priority-badge priority-${getCasePriorityColor(c.priority)}`}>
                    {getCasePriorityLabel(c.priority)}
                  </span>
                  <span className="case-id">#{c.id.slice(0, 8)}</span>
                </div>
                
                <p className="queue-item-title">
                  {c.case_type.replace(/_/g, ' ')}
                </p>
                
                {c.customer && (
                  <p className="queue-item-customer">
                    {c.customer.first_name} {c.customer.last_name}
                  </p>
                )}
                
                <div className="queue-item-footer">
                  <span className="queue-item-time">
                    Assigned {getTimeAgo(c.assigned_at || c.created_at)}
                  </span>
                  
                  {c.sla_due_at && (
                    <span className={`sla-badge sla-${slaStatus.status}`}>
                      {slaStatus.status === 'overdue'
                        ? 'Overdue'
                        : `${Math.round(slaStatus.hours_remaining)}h left`}
                    </span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
      
      {cases.length > 0 && (
        <Link to="/cases?filter=assigned_to_me" className="priority-queue-view-all">
          View All My Cases →
        </Link>
      )}
    </div>
  );
};

const getTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins < 60) return `${diffMins}m ago`;
  
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
};

